'use client';

import { Trash2 } from 'lucide-react';
import { useTranslations } from 'next-intl';

import {
  AVATAR_TEXT_COLOR,
  getAvatarColor,
  getAvatarInitial,
} from '@/utils/avatarColor';
import { formatRelativeTime } from '@/utils/formatRelativeTime';
import { WIX_USER_NAME_SEPARATOR } from '@/utils/replierDisplayName';
import { translateDisplayName } from '@/utils/translateDisplayName';

import type { CommentReply, CommentUserInfo } from '@/types/feedback';

interface ReplyItemProps {
  reply: CommentReply;
  /** The commenter of the comment this reply is on. */
  commenterId?: string | null;
  commenterInfoMap?: Record<string, CommentUserInfo>;
  /** Shown for a Wix user reply with no resolved name (a legacy reply). */
  agencyName?: string;
  currentUserId?: string | null;
  onDelete?: () => void;
}

/** Only `replier` says who wrote a reply; `replierId` is deprecated. */
function isCommenterReply(reply: CommentReply): boolean {
  return reply.replier?.type === 'COMMENTER';
}

/**
 * The reviewer deletes their own replies (every commenter reply on their
 * page is theirs); a Wix user deletes the replies they wrote (owner payloads
 * carry `wixUserOptions.userId`).
 */
function isOwnReply(
  reply: CommentReply,
  commenterId: string | null | undefined,
  currentUserId: string | null | undefined,
): boolean {
  if (!currentUserId) return false;
  return isCommenterReply(reply)
    ? currentUserId === commenterId
    : reply.replier?.wixUserOptions?.userId === currentUserId;
}

export function ReplyItem({
  reply,
  commenterId,
  commenterInfoMap,
  agencyName,
  currentUserId,
  onDelete,
}: ReplyItemProps) {
  const t = useTranslations();
  // An unresolved author renders the shared Guest fallback instead of
  // exposing an internal id.
  const rawName = isCommenterReply(reply)
    ? commenterId
      ? commenterInfoMap?.[commenterId]?.userName
      : undefined
    : reply.replier?.displayName || agencyName;
  const displayName = translateDisplayName(rawName, t);
  // Initial and color come from the untranslated name so they stay stable
  // across locales, matching the pin and the comments panel. A Wix user's
  // initial comes from their own name alone and their color from their user
  // id (when known), so the avatar doesn't change while an optimistic reply's
  // name becomes the server's "user @ account".
  const avatarName = isCommenterReply(reply)
    ? rawName
    : rawName?.split(WIX_USER_NAME_SEPARATOR)[0];
  const avatarSeed =
    (!isCommenterReply(reply) && reply.replier?.wixUserOptions?.userId) ||
    avatarName;
  const initial = getAvatarInitial(avatarName);
  const ts = reply.createdDate ? new Date(reply.createdDate).getTime() : null;
  // A local-only reply is not persisted yet, so its index may not match the
  // server's list.
  const canDelete =
    !reply.isLocalOnly &&
    onDelete &&
    isOwnReply(reply, commenterId, currentUserId);

  return (
    <div className="group flex items-start gap-2 px-3 py-2">
      <span
        className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-[11px] font-semibold"
        style={{
          backgroundColor: getAvatarColor(avatarSeed),
          color: AVATAR_TEXT_COLOR,
        }}
      >
        {initial}
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-semibold text-foreground leading-tight">
            {displayName}
          </span>
          {ts && (
            <span className="text-xs text-muted-foreground">
              {formatRelativeTime(ts, t)}
            </span>
          )}
        </div>
        <p className="text-xs text-foreground leading-relaxed break-words">
          {reply.message}
        </p>
      </div>
      {canDelete && (
        <button
          type="button"
          aria-label={t('get-feedback-pinReply-delete')}
          onClick={onDelete}
          className="mt-0.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
        >
          <Trash2 size={13} />
        </button>
      )}
    </div>
  );
}
